// src/App.js

import React, { useEffect, useState } from 'react';
import Logo from './assets/Logo.png';
import LogoNB from './assets/LogoNB.png';
import { BrowserRouter as Router, Route, Routes, Link } from 'react-router-dom';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  setPersistence,
  browserLocalPersistence,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';

import { auth, db } from './firebase';

import StockTransfer from './components/StockTransfer';
import ProductionLog from './components/ProductionLog';
import StockViewer from './components/StockViewer';
import RecipeViewer from './components/RecipeViewer';
import RecipeEditor from './components/RecipeEditor';
import GelatoCostCalculator from './components/GelatoCostCalculator';
import ProductionLogViewer from './components/ProductionLogViewer';
import UploadSalesCSV from './components/UploadSalesCSV';
import DrinksStockViewer from './components/DrinksStockViewer';
import DrinksEditor from './components/DrinksEditor';
import ProfitDashboard from './components/ProfitDashboard';
import IngredientUsageReport from './components/IngredientUsageReport';
import RecipeOrderPlanner from './components/RecipeOrderPlanner';
import SmartOrderPlanner from './components/SmartOrderPlanner';

import './App.css';

function App() {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [login, setLogin] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);

      if (firebaseUser) {
        const snap = await getDoc(doc(db, 'users', firebaseUser.uid));
        setIsAdmin(snap.exists() && snap.data().role === 'admin');
      } else {
        setIsAdmin(false);
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleLogin = async (e) => {
    if (e?.preventDefault) e.preventDefault();

    try {
      await setPersistence(auth, browserLocalPersistence);
      await signInWithEmailAndPassword(auth, login.email, login.password);
    } catch {
      alert('❌ Login failed');
    }
  };

  const handleLogout = () => {
    signOut(auth);
    setLogin({ email: '', password: '' });
  };

  if (loading) {
    return (
      <div className="page page-center">
        <div className="spinner" /> Loading…
      </div>
    );
  }

  if (!user) {
    return (
      <div className="page page-center">
        <div className="login-wrapper">
          <img src={LogoNB} alt="Evergreen Gelateria" className="logo-big" />

          <div className="card card-sm login-card">
            <h2 className="title">🔐 Login</h2>

            <form className="stack" onSubmit={handleLogin}>
              <input
                className="input"
                type="email"
                placeholder="Email"
                value={login.email}
                autoComplete="email"
                onChange={(e) =>
                  setLogin({ ...login, email: e.target.value })
                }
              />

              <input
                className="input"
                type="password"
                placeholder="Password"
                value={login.password}
                autoComplete="current-password"
                onChange={(e) =>
                  setLogin({ ...login, password: e.target.value })
                }
              />

              <button className="btn btn-primary" type="submit">
                🔓 Login
              </button>
            </form>
          </div>
        </div>

        <footer className="footer">
          ©{new Date().getFullYear()} Evergreen
        </footer>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="page">
        <header className="appbar">
          <div className="brand">
            <img src={Logo} alt="Evergreen Gelateria" className="logo" />
            <span>Evergreen Stock Manager</span>
          </div>

          <button className="btn btn-ghost" onClick={handleLogout}>
            🔒 Logout
          </button>
        </header>

        <main className="container">
          <h2 className="title">🧾 Gelato Production Log</h2>
          <div className="card">
            <ProductionLog />
          </div>
        </main>
      </div>
    );
  }

  return (
    <Router>
      <div className="page">
        <header className="appbar">
          <div className="brand">
            <img src={Logo} alt="Evergreen Gelateria" className="logo" />
            <span>Evergreen Stock Manager</span>
          </div>

          <button className="btn btn-ghost" onClick={handleLogout}>
            🔒 Logout
          </button>
        </header>

        <nav className="nav">
          <div className="nav-links desktop-only">
            <Link to="/" className="nav-link">🏠 Home</Link>
            <Link to="/transfer" className="nav-link">📦 Stock Transfer</Link>
            <Link to="/production" className="nav-link">🧾 Production Log</Link>
            <Link to="/usage-report" className="nav-link">📦 Usage Report</Link>
            <Link to="/order-planner" className="nav-link">🛒 Order Planner</Link>
            <Link to="/smart-order" className="nav-link">🧠 Smart Order</Link>
            <Link to="/stock" className="nav-link">📊 Ingredients Stock</Link>
            <Link to="/drinks" className="nav-link">🥤 Drinks Stock</Link>
            <Link to="/edit-drinks" className="nav-link">✏️ Edit Drinks</Link>
            <Link to="/edit-recipes" className="nav-link">✏️ Edit Recipes</Link>
            <Link to="/recipes" className="nav-link">📖 View Recipes</Link>
            <Link to="/costs" className="nav-link">💸 Gelato Costs</Link>
            <Link to="/profit" className="nav-link">📈 Profit Dashboard</Link>
            <Link to="/logs" className="nav-link">🕓 Production Logs</Link>
            <Link to="/upload-sales" className="nav-link">⬆️ Upload Sales CSV</Link>
          </div>

          <div className="mobile-only">
            <button
              className="hamburger"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              ☰
            </button>

            {isMenuOpen && (
              <div className="mobile-menu">
                <Link to="/" onClick={() => setIsMenuOpen(false)}>🏠 Home</Link>
                <Link to="/transfer" onClick={() => setIsMenuOpen(false)}>📦 Stock Transfer</Link>
                <Link to="/production" onClick={() => setIsMenuOpen(false)}>🧾 Production Log</Link>
                <Link to="/usage-report" onClick={() => setIsMenuOpen(false)}>📦 Usage Report</Link>
                <Link to="/order-planner" onClick={() => setIsMenuOpen(false)}>🛒 Order Planner</Link>
                <Link to="/smart-order" onClick={() => setIsMenuOpen(false)}>🧠 Smart Order</Link>
                <Link to="/stock" onClick={() => setIsMenuOpen(false)}>📊 Ingredients Stock</Link>
                <Link to="/drinks" onClick={() => setIsMenuOpen(false)}>🥤 Drinks Stock</Link>
                <Link to="/edit-drinks" onClick={() => setIsMenuOpen(false)}>✏️ Edit Drinks</Link>
                <Link to="/edit-recipes" onClick={() => setIsMenuOpen(false)}>✏️ Edit Recipes</Link>
                <Link to="/recipes" onClick={() => setIsMenuOpen(false)}>📖 View Recipes</Link>
                <Link to="/costs" onClick={() => setIsMenuOpen(false)}>💸 Gelato Costs</Link>
                <Link to="/profit" onClick={() => setIsMenuOpen(false)}>📈 Profit Dashboard</Link>
                <Link to="/logs" onClick={() => setIsMenuOpen(false)}>🕓 Production Logs</Link>
                <Link to="/upload-sales" onClick={() => setIsMenuOpen(false)}>⬆️ Upload Sales CSV</Link>
              </div>
            )}
          </div>
        </nav>

        <main className="container">
          <div className="card">
            <Routes>
              <Route
                path="/"
                element={
                  <div>
                    <h2 className="title">
                      Welcome to Evergreen Gelateria’s Stock Manager 🍨
                    </h2>
                    <p>
                      Track inventory, log production, manage recipes, and calculate costs all in one place.
                    </p>
                  </div>
                }
              />

              <Route path="/transfer" element={<StockTransfer />} />
              <Route path="/production" element={<ProductionLog />} />
              <Route path="/usage-report" element={<IngredientUsageReport />} />
              <Route path="/order-planner" element={<RecipeOrderPlanner />} />
              <Route path="/smart-order" element={<SmartOrderPlanner />} />
              <Route path="/stock" element={<StockViewer />} />
              <Route path="/drinks" element={<DrinksStockViewer />} />
              <Route path="/edit-drinks" element={<DrinksEditor />} />
              <Route path="/edit-recipes" element={<RecipeEditor />} />
              <Route path="/recipes" element={<RecipeViewer />} />
              <Route path="/costs" element={<GelatoCostCalculator />} />
              <Route path="/profit" element={<ProfitDashboard />} />
              <Route path="/logs" element={<ProductionLogViewer />} />
              <Route path="/upload-sales" element={<UploadSalesCSV />} />
            </Routes>
          </div>
        </main>

        <footer className="footer">
          ©{new Date().getFullYear()} Evergreen
        </footer>
      </div>
    </Router>
  );
}

export default App;